"use client";

// ============================================================
// CLUBHUB – Dokumente: die Dialoge der Ablage
// ============================================================
// Ordner anlegen, umbenennen, verschieben, Rechte setzen; Dateien
// umbenennen, verschieben, teilen; Ziel fuer ein Hochladen von der
// Startseite. Alle aus den Teilen von `ui/dialog` und mit der
// Mechanik von `DialogForm` — Pruefung vor dem Speichern, Fehler am
// Feld, Erfolg als Toast.
//
// Wo eine Aenderung mehr Menschen etwas zeigt als vorher (ein
// Ordner wird geoeffnet, Dateien wandern nach „Öffentlich“), sagt
// der Dialog das VOR dem Speichern — mit Zahlen, nicht mit
// „Achtung“.
//
// Jeder Dialog ruft zuerst die Server Action (`../actions.ts`); erst
// wenn sie Erfolg meldet, steht die Aenderung auch im Browser. Mit
// Sitzung frischt `DialogForm` danach die Seite auf (`refresh`).
// ============================================================

import { useMemo, useState } from "react";
import { ChevronRight, Folder, Globe, Lock, ShieldCheck, Users, UsersRound, type LucideIcon } from "lucide-react";
import type { Visibility } from "@/generated/prisma/enums";
import {
  Button,
  Checkbox,
  DateInput,
  Dialog,
  DialogCancel,
  DialogChoice,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogNotice,
  DialogSubmit,
  DialogTitle,
  FieldLabel,
  Input,
  Select,
  toast,
} from "@/components/ui";
import { VISIBILITY } from "@/lib/labels";
import { cn, formatDate } from "@/lib/utils";
import { formatDecimal } from "@/lib/number-format";
import {
  addTage,
  BEARBEITEN_DUERFEN,
  dateiArt,
  endung,
  freierName,
  grundname,
  jetztIso,
  kinderVon,
  nachkommen,
  namePruefen,
  pfadText,
  SICHT_RANG,
  sehenDuerfen,
  wirksameSicht,
  type Sicht,
} from "@/lib/dokumente/format";
import type { FolderView, ShareLinkView } from "@/lib/dokumente/views";
import { mitDatei, mitOrdner, neueId } from "./ablage-zustand";
import { useAblage } from "./ablage-kontext";
import {
  dateiAendern,
  dateienVerschieben,
  linkErstellen,
  ordnerAnlegen,
  ordnerRechteSetzen,
  ordnerUmbenennen,
  ordnerVerschieben,
} from "@/app/(app)/dokumente/actions";
import { istFehler } from "@/lib/action-result";

export const SICHT_ZEICHEN: Record<Visibility, LucideIcon> = {
  PUBLIC: Globe,
  MEMBERS: Users,
  GROUPS: UsersRound,
  BOARD: ShieldCheck,
  PRIVATE: Lock,
};

const REIHE: Visibility[] = ["PUBLIC", "MEMBERS", "GROUPS", "BOARD", "PRIVATE"];

/** Alle Ordner als Auswahl, mit Pfad — „Verein › Fotos und Videos › Sommerfest 2025“. */
function ordnerOptionen(folders: FolderView[], ausser: Set<string> = new Set()) {
  const liste: { value: string; label: string; group?: string }[] = [];
  const laufen = (parentId: string | null, laufwerk: string) => {
    for (const f of kinderVon(parentId, folders)) {
      if (ausser.has(f.id)) continue;
      const lw = parentId === null ? f.name : laufwerk;
      liste.push({ value: f.id, label: parentId === null ? f.name : pfadText(f.id, folders).split(" › ").slice(1).join(" › "), group: lw });
      laufen(f.id, lw);
    }
  };
  laufen(null, "");
  return liste;
}

// ── Sichtbarkeit waehlen ─────────────────────────────────────

/**
 * Stufe und Gruppen fuer einen Ordner, darunter der Satz, wer ihn
 * danach sieht. Angeboten wird nur, was nicht weiter ist als der
 * uebergeordnete Ordner — ein Unterordner kann einschraenken, nicht
 * oeffnen.
 */
function SichtWahl({
  eltern,
  wert,
  gruppen,
  onWert,
  onGruppen,
  fehler,
  urheber,
}: {
  eltern: Sicht;
  wert: Visibility;
  gruppen: string[];
  onWert: (v: Visibility) => void;
  onGruppen: (ids: string[]) => void;
  fehler?: string | null;
  urheber: { id: string; name: string } | null;
}) {
  const { daten } = useAblage();
  const erlaubt = REIHE.filter((v) => SICHT_RANG[v] >= SICHT_RANG[eltern.visibility]);
  const gruppenAngebot = eltern.visibility === "GROUPS" ? daten.groups.filter((g) => eltern.groupIds.includes(g.id)) : daten.groups;
  const wirksam = {
    visibility: wert,
    groupIds: wert === "GROUPS" && eltern.visibility === "GROUPS" ? gruppen.filter((g) => eltern.groupIds.includes(g)) : gruppen,
    createdBy: urheber,
  };
  const aussage = sehenDuerfen(wirksam, daten);
  const Zeichen = SICHT_ZEICHEN[wert];

  return (
    <>
      <DialogChoice
        label="Sichtbar für"
        value={wert}
        onChange={onWert}
        options={erlaubt.map((v) => ({
          value: v,
          label: VISIBILITY[v].label,
          hint: v === "PRIVATE" && urheber ? `Nur ${urheber.name}` : VISIBILITY[v].hinweis,
        }))}
      />
      {erlaubt.length < REIHE.length && (
        <p className="text-caption text-text-tertiary -mt-2">
          Weiter als der übergeordnete Ordner geht nicht: dort sehen {sehenDuerfen(eltern, daten).kurz.toLocaleLowerCase("de")}.
        </p>
      )}

      {wert === "GROUPS" && (
        <div className="space-y-2">
          <FieldLabel>Gruppen</FieldLabel>
          <div className={cn("grid grid-cols-1 gap-x-4 gap-y-2 rounded-lg border p-3 sm:grid-cols-2", fehler ? "border-danger" : "border-border")}>
            {gruppenAngebot.map((g) => (
              <Checkbox
                key={g.id}
                checked={gruppen.includes(g.id)}
                onChange={(e) => onGruppen(e.target.checked ? [...gruppen, g.id] : gruppen.filter((x) => x !== g.id))}
                label={g.name}
                hint={`${formatDecimal(g.memberCount)} ${g.memberCount === 1 ? "Person" : "Personen"}`}
              />
            ))}
          </div>
          {fehler && <p className="text-caption text-danger">{fehler}</p>}
        </div>
      )}

      <DialogNotice tone={wert === "PUBLIC" ? "warning" : "neutral"} icon={<Zeichen className="text-text-secondary h-4 w-4" aria-hidden />}>
        <p>
          <span className="font-medium">Sehen dürfen:</span> {aussage.satz}
        </p>
        <p className="text-text-secondary mt-1">
          <span className="font-medium">Bearbeiten dürfen:</span> {BEARBEITEN_DUERFEN}
        </p>
      </DialogNotice>
    </>
  );
}

// ── Ordner anlegen ───────────────────────────────────────────
export function OrdnerNeuDialog({ parentId, onClose }: { parentId: string | null; onClose: () => void }) {
  const { bestand, daten, aendern, zumOrdner } = useAblage();
  const optionen = useMemo(() => ordnerOptionen(bestand.folders), [bestand.folders]);
  const [eltern, setEltern] = useState(
    parentId && bestand.folders.some((f) => f.id === parentId) ? parentId : (optionen[0]?.value ?? ""),
  );
  const elternSicht = wirksameSicht(eltern, bestand.folders);
  const [name, setName] = useState("");
  const [wert, setWert] = useState<Visibility>(elternSicht.visibility);
  const [gruppen, setGruppen] = useState<string[]>(elternSicht.groupIds);
  const [fehler, setFehler] = useState<{ feld: string; text: string } | null>(null);

  function elternWechseln(id: string) {
    setEltern(id);
    const s = wirksameSicht(id, bestand.folders);
    setWert(s.visibility);
    setGruppen(s.groupIds);
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogForm
        refresh={daten.quelle === "datenbank"}
        action={async () => {
          const geschwister = kinderVon(eltern, bestand.folders).map((f) => f.name);
          const nameFehler = namePruefen(name, geschwister, "Ordner");
          if (nameFehler) return { error: nameFehler, field: "name" };
          if (wert === "GROUPS" && gruppen.length === 0) return { error: "Bitte mindestens eine Gruppe wählen.", field: "gruppen" };
          const r = await ordnerAnlegen({ parentId: eltern, name, visibility: wert, groupIds: wert === "GROUPS" ? gruppen : [] });
          if (istFehler(r)) return r;
          const id = r.id ?? neueId("o");
          aendern((b) => ({
            ...b,
            folders: [
              ...b.folders,
              {
                id,
                parentId: eltern,
                name: name.trim(),
                visibility: wert,
                groupIds: wert === "GROUPS" ? gruppen : [],
                sortOrder: kinderVon(eltern, b.folders).length,
                createdAt: jetztIso(),
                createdBy: daten.me,
              },
            ],
          }));
          return { success: true as const, id };
        }}
        success={() => `Ordner „${name.trim()}“ angelegt`}
        onSuccess={(r) => {
          if ("id" in r && r.id) zumOrdner(r.id);
        }}
        onError={(text, r) => setFehler({ feld: (r as { field?: string } | undefined)?.field ?? "name", text })}
      >
        <DialogHeader>
          <DialogTitle>Neuer Ordner</DialogTitle>
          <DialogDescription>Ein Ordner gehört immer zu einem der drei Laufwerke und sieht höchstens so weit wie dieses.</DialogDescription>
        </DialogHeader>
        <DialogContent>
          <Input
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (fehler?.feld === "name") setFehler(null);
            }}
            placeholder="z. B. Hallenturnier 2027"
            error={fehler?.feld === "name" ? fehler.text : undefined}
            autoComplete="off"
          />
          <Select label="Liegt in" value={eltern} onChange={elternWechseln} options={optionen} searchable />
          <SichtWahl
            eltern={elternSicht}
            wert={wert}
            gruppen={gruppen}
            onWert={setWert}
            onGruppen={(g) => {
              setGruppen(g);
              if (fehler?.feld === "gruppen") setFehler(null);
            }}
            fehler={fehler?.feld === "gruppen" ? fehler.text : null}
            urheber={daten.me}
          />
        </DialogContent>
        <DialogFooter>
          <DialogCancel />
          <DialogSubmit>Ordner anlegen</DialogSubmit>
        </DialogFooter>
      </DialogForm>
    </Dialog>
  );
}

// ── Ordner umbenennen ────────────────────────────────────────
export function OrdnerUmbenennenDialog({ folderId, onClose }: { folderId: string; onClose: () => void }) {
  const { bestand, daten, aendern } = useAblage();
  const ordner = bestand.folders.find((f) => f.id === folderId);
  const [name, setName] = useState(ordner?.name ?? "");
  const [fehler, setFehler] = useState<string | null>(null);
  if (!ordner) return null;

  return (
    <Dialog open onClose={onClose}>
      <DialogForm
        refresh={daten.quelle === "datenbank"}
        action={async () => {
          const geschwister = kinderVon(ordner.parentId, bestand.folders).filter((f) => f.id !== ordner.id).map((f) => f.name);
          const nameFehler = namePruefen(name, geschwister, "Ordner");
          if (nameFehler) return { error: nameFehler };
          const r = await ordnerUmbenennen(ordner.id, name);
          if (istFehler(r)) return r;
          aendern((b) => mitOrdner(b, ordner.id, (f) => ({ ...f, name: name.trim() })));
          return { success: true as const };
        }}
        success="Umbenannt"
        onError={setFehler}
      >
        <DialogHeader>
          <DialogTitle>Ordner umbenennen</DialogTitle>
        </DialogHeader>
        <DialogContent>
          <Input
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setFehler(null);
            }}
            onFocus={(e) => e.currentTarget.select()}
            error={fehler ?? undefined}
            autoComplete="off"
          />
        </DialogContent>
        <DialogFooter>
          <DialogCancel />
          <DialogSubmit>Speichern</DialogSubmit>
        </DialogFooter>
      </DialogForm>
    </Dialog>
  );
}

// ── Rechte eines Ordners ─────────────────────────────────────
export function RechteDialog({ folderId, onClose }: { folderId: string; onClose: () => void }) {
  const { bestand, daten, aendern } = useAblage();
  const ordner = bestand.folders.find((f) => f.id === folderId);
  const elternSicht = wirksameSicht(ordner?.parentId ?? null, bestand.folders);
  // Startwert ist, wer WIRKLICH sieht: eine gespeicherte Stufe, die
  // weiter ist als der Elternordner (etwa nach dem Verschieben), gilt
  // nicht und steht deshalb auch nicht zur Wahl.
  const jetzt = wirksameSicht(folderId, bestand.folders);
  const [wert, setWert] = useState<Visibility>(jetzt.visibility);
  const [gruppen, setGruppen] = useState<string[]>(jetzt.groupIds);
  const [fehler, setFehler] = useState<string | null>(null);
  if (!ordner) return null;

  const istLaufwerk = ordner.parentId === null;
  const unter = nachkommen(ordner.id, bestand.folders);
  const alle = new Set([ordner.id, ...unter]);
  const dateien = bestand.documents.filter((d) => d.folderId && alle.has(d.folderId));
  const bilder = dateien.filter((d) => dateiArt(d.mimeType, d.name) === "bild").length;
  const oeffnet =
    SICHT_RANG[wert] < SICHT_RANG[jetzt.visibility] ||
    (wert === "GROUPS" && jetzt.visibility === "GROUPS" && gruppen.some((g) => !jetzt.groupIds.includes(g)));

  if (istLaufwerk || !daten.darfSchreiben) {
    const aussage = sehenDuerfen(wirksameSicht(ordner.id, bestand.folders), daten);
    const Zeichen = SICHT_ZEICHEN[wirksameSicht(ordner.id, bestand.folders).visibility];
    return (
      <Dialog open onClose={onClose}>
        <DialogHeader>
          <DialogTitle>{istLaufwerk ? `Rechte des Laufwerks „${ordner.name}“` : `Wer „${ordner.name}“ sehen darf`}</DialogTitle>
          <DialogDescription>
            {istLaufwerk
              ? "Die drei Laufwerke haben feste Rechte, wie bisher in ClubDesk. Jeder Ordner darin lässt sich enger einstellen — etwa nur für eine Mannschaft."
              : "Ändern können das Vorstand, Verwaltung und Schriftführung."}
          </DialogDescription>
        </DialogHeader>
        <DialogContent>
          <DialogNotice icon={<Zeichen className="text-text-secondary h-4 w-4" aria-hidden />}>
            <p>
              <span className="font-medium">Sehen dürfen:</span> {aussage.satz}
            </p>
            <p className="text-text-secondary mt-1">
              <span className="font-medium">Bearbeiten dürfen:</span> {BEARBEITEN_DUERFEN}
            </p>
          </DialogNotice>
          <p className="text-body-sm text-text-secondary tabular-nums">
            {formatDecimal(unter.length)} Ordner und {formatDecimal(dateien.length)} Dateien liegen darin.
          </p>
        </DialogContent>
        <DialogFooter>
          <DialogCancel>Schließen</DialogCancel>
        </DialogFooter>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogForm
        refresh={daten.quelle === "datenbank"}
        action={async () => {
          if (wert === "GROUPS" && gruppen.length === 0) return { error: "Bitte mindestens eine Gruppe wählen." };
          const r = await ordnerRechteSetzen(ordner.id, wert, wert === "GROUPS" ? gruppen : []);
          if (istFehler(r)) return r;
          aendern((b) => mitOrdner(b, ordner.id, (f) => ({ ...f, visibility: wert, groupIds: wert === "GROUPS" ? gruppen : [] })));
          return { success: true as const };
        }}
        success={() => ({
          title: "Rechte gespeichert",
          description: `Sehen dürfen: ${sehenDuerfen({ visibility: wert, groupIds: gruppen, createdBy: ordner.createdBy ?? daten.me }, daten).kurz}`,
        })}
        onError={setFehler}
      >
        <DialogHeader>
          <DialogTitle>Wer „{ordner.name}“ sehen darf</DialogTitle>
          <DialogDescription>
            Gilt für die Dateien darin und für {unter.length === 1 ? "den Unterordner" : `${formatDecimal(unter.length)} Unterordner`}, soweit sie nicht enger eingestellt sind.
          </DialogDescription>
        </DialogHeader>
        <DialogContent>
          <SichtWahl
            eltern={elternSicht}
            wert={wert}
            gruppen={gruppen}
            onWert={(v) => {
              setWert(v);
              setFehler(null);
            }}
            onGruppen={(g) => {
              setGruppen(g);
              setFehler(null);
            }}
            fehler={fehler}
            urheber={ordner.createdBy ?? daten.me}
          />
          {oeffnet && dateien.length > 0 && (
            <DialogNotice tone="warning">
              Damit sehen mehr Menschen {formatDecimal(dateien.length)} {dateien.length === 1 ? "Datei" : "Dateien"} als bisher.
              {wert === "PUBLIC" && bilder > 0 &&
                ` Darunter ${formatDecimal(bilder)} Fotos — ${formatDecimal(daten.ohneFotoEinwilligung)} Mitglieder haben Fotos im Internet nicht zugestimmt.`}
            </DialogNotice>
          )}
        </DialogContent>
        <DialogFooter>
          <DialogCancel />
          <DialogSubmit>Rechte speichern</DialogSubmit>
        </DialogFooter>
      </DialogForm>
    </Dialog>
  );
}

// ── Verschieben ──────────────────────────────────────────────
/** Der Baum als Liste zum Antippen — gesperrt, was nicht Ziel sein kann. */
function Zielbaum({ wert, onWert, gesperrt }: { wert: string; onWert: (id: string) => void; gesperrt: Set<string> }) {
  const { bestand } = useAblage();
  const zeilen: { f: FolderView; tiefe: number }[] = [];
  const laufen = (parentId: string | null, tiefe: number) => {
    for (const f of kinderVon(parentId, bestand.folders)) {
      zeilen.push({ f, tiefe });
      laufen(f.id, tiefe + 1);
    }
  };
  laufen(null, 0);

  return (
    <div role="radiogroup" aria-label="Zielordner" className="border-border max-h-72 overflow-y-auto rounded-lg border p-1">
      {zeilen.map(({ f, tiefe }) => {
        const aus = gesperrt.has(f.id);
        const an = wert === f.id;
        const Zeichen = f.parentId === null ? SICHT_ZEICHEN[f.visibility] : Folder;
        return (
          <button
            key={f.id}
            type="button"
            role="radio"
            aria-checked={an}
            disabled={aus}
            onClick={() => onWert(f.id)}
            style={{ paddingLeft: `${8 + tiefe * 16}px` }}
            className={cn(
              "text-body-sm flex w-full items-center gap-2 rounded-md py-1.5 pr-2 text-left",
              "duration-fast transition-colors",
              an ? "bg-accent-subtle text-text-primary font-medium" : "text-text-secondary hover:bg-bg-secondary",
              aus && "cursor-not-allowed opacity-40 hover:bg-transparent",
            )}
          >
            <Zeichen className="text-text-tertiary h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="truncate">{f.name}</span>
          </button>
        );
      })}
    </div>
  );
}

function VerschiebenHinweis({ vonSicht, ziel, bilder }: { vonSicht: Sicht; ziel: Sicht | null; bilder: number }) {
  const { daten } = useAblage();
  if (!ziel) return null;
  const weiter = SICHT_RANG[ziel.visibility] < SICHT_RANG[vonSicht.visibility];
  return (
    <DialogNotice tone={weiter ? "warning" : "neutral"}>
      <p>
        <span className="font-medium">Danach sehen:</span> {sehenDuerfen(ziel, daten).satz}
      </p>
      {weiter && <p className="mt-1">Das sind mehr Menschen als bisher ({sehenDuerfen(vonSicht, daten).kurz}).</p>}
      {ziel.visibility === "PUBLIC" && bilder > 0 && (
        <p className="mt-1">
          {formatDecimal(daten.ohneFotoEinwilligung)} Mitglieder haben Fotos im Internet nicht zugestimmt — bitte nur Bilder, auf denen sie nicht zu erkennen sind.
        </p>
      )}
    </DialogNotice>
  );
}

export function DateienVerschiebenDialog({ dateiIds, onClose, onFertig }: { dateiIds: string[]; onClose: () => void; onFertig?: () => void }) {
  const { bestand, daten, aendern, zumOrdner } = useAblage();
  const dateien = bestand.documents.filter((d) => dateiIds.includes(d.id));
  const herkunft = new Set(dateien.map((d) => d.folderId ?? ""));
  const einzigerOrt = herkunft.size === 1 ? [...herkunft][0]! : "";
  const [ziel, setZiel] = useState("");
  const vonSicht = wirksameSicht(einzigerOrt || dateien[0]?.folderId || null, bestand.folders);
  const bilder = dateien.filter((d) => dateiArt(d.mimeType, d.name) === "bild").length;
  // Was schon im Ziel liegt, wandert nicht — und bekommt erst recht keinen Zusatz „(2)“.
  const wandern = dateien.filter((d) => d.folderId !== ziel);
  const vorhanden = bestand.documents.filter((d) => d.folderId === ziel).map((d) => d.name);
  const umbenannt = wandern.filter((d) => vorhanden.some((v) => v.toLocaleLowerCase("de") === d.name.toLocaleLowerCase("de"))).length;

  return (
    <Dialog open onClose={onClose}>
      <DialogForm
        refresh={daten.quelle === "datenbank"}
        action={async () => {
          if (!ziel) return { error: "Bitte einen Zielordner wählen." };
          if (wandern.length === 0) return { error: "Die Dateien liegen schon dort." };
          const r = await dateienVerschieben(
            wandern.map((d) => d.id),
            ziel,
          );
          if (istFehler(r)) return r;
          const belegt = [...vorhanden];
          const neueNamen = new Map<string, string>();
          for (const d of wandern) {
            const n = freierName(d.name, belegt);
            belegt.push(n);
            neueNamen.set(d.id, n);
          }
          // Im Speicher bleibt der Schluessel — nur Ordner und gegebenenfalls Name aendern sich.
          aendern((b) => ({
            ...b,
            documents: b.documents.map((d) => (neueNamen.has(d.id) ? { ...d, folderId: ziel, name: neueNamen.get(d.id)! } : d)),
          }));
          return { success: true as const };
        }}
        onSuccess={() => {
          onFertig?.();
          toast.success(`${wandern.length === 1 ? "Datei" : `${formatDecimal(wandern.length)} Dateien`} verschoben`, {
            description: pfadText(ziel, bestand.folders),
            action: { label: "Zum Ordner", onClick: () => zumOrdner(ziel) },
          });
        }}
        onError={(t) => toast.error(t)}
      >
        <DialogHeader>
          <DialogTitle>{dateien.length === 1 ? `„${dateien[0]!.name}“ verschieben` : `${formatDecimal(dateien.length)} Dateien verschieben`}</DialogTitle>
          {einzigerOrt && <DialogDescription>Liegt jetzt in {pfadText(einzigerOrt, bestand.folders)}.</DialogDescription>}
        </DialogHeader>
        <DialogContent>
          <Zielbaum wert={ziel} onWert={setZiel} gesperrt={new Set(einzigerOrt ? [einzigerOrt] : [])} />
          <VerschiebenHinweis vonSicht={vonSicht} ziel={ziel ? wirksameSicht(ziel, bestand.folders) : null} bilder={bilder} />
          {umbenannt > 0 && (
            <p className="text-caption text-text-tertiary">
              {umbenannt === 1 ? "Eine Datei bekommt" : `${formatDecimal(umbenannt)} Dateien bekommen`} einen Zusatz wie „(2)“, weil es den Namen dort schon gibt.
            </p>
          )}
        </DialogContent>
        <DialogFooter>
          <DialogCancel />
          <DialogSubmit disabled={!ziel}>Verschieben</DialogSubmit>
        </DialogFooter>
      </DialogForm>
    </Dialog>
  );
}

export function OrdnerVerschiebenDialog({ folderId, onClose }: { folderId: string; onClose: () => void }) {
  const { bestand, daten, aendern } = useAblage();
  const ordner = bestand.folders.find((f) => f.id === folderId);
  const [ziel, setZiel] = useState("");
  if (!ordner) return null;
  const gesperrt = new Set([ordner.id, ...nachkommen(ordner.id, bestand.folders), ordner.parentId ?? ""]);
  const namensgleich = ziel && kinderVon(ziel, bestand.folders).some((f) => f.name.toLocaleLowerCase("de") === ordner.name.toLocaleLowerCase("de"));
  const alle = new Set([ordner.id, ...nachkommen(ordner.id, bestand.folders)]);
  const bilder = bestand.documents.filter((d) => d.folderId && alle.has(d.folderId) && dateiArt(d.mimeType, d.name) === "bild").length;

  return (
    <Dialog open onClose={onClose}>
      <DialogForm
        refresh={daten.quelle === "datenbank"}
        action={async () => {
          if (!ziel) return { error: "Bitte einen Zielordner wählen." };
          if (namensgleich) return { error: `Dort gibt es schon einen Ordner „${ordner.name}“.` };
          // Kreise verhindert die Action ein zweites Mal.
          const r = await ordnerVerschieben(ordner.id, ziel);
          if (istFehler(r)) return r;
          aendern((b) => mitOrdner(b, ordner.id, (f) => ({ ...f, parentId: ziel, sortOrder: kinderVon(ziel, b.folders).length })));
          return { success: true as const };
        }}
        success={() => ({ title: `„${ordner.name}“ verschoben`, description: pfadText(ziel, bestand.folders) })}
        onError={(t) => toast.error(t)}
      >
        <DialogHeader>
          <DialogTitle>„{ordner.name}“ verschieben</DialogTitle>
          <DialogDescription>Mit allem, was darin liegt. Eigene Rechte des Ordners bleiben, werden aber höchstens so weit wie im Ziel.</DialogDescription>
        </DialogHeader>
        <DialogContent>
          <Zielbaum wert={ziel} onWert={setZiel} gesperrt={gesperrt} />
          {namensgleich ? (
            <DialogNotice tone="danger">Dort gibt es schon einen Ordner „{ordner.name}“ — bitte vorher umbenennen.</DialogNotice>
          ) : (
            <VerschiebenHinweis
              vonSicht={wirksameSicht(ordner.id, bestand.folders)}
              ziel={ziel ? wirksameSicht(ordner.id, bestand.folders.map((f) => (f.id === ordner.id ? { ...f, parentId: ziel } : f))) : null}
              bilder={bilder}
            />
          )}
        </DialogContent>
        <DialogFooter>
          <DialogCancel />
          <DialogSubmit disabled={!ziel || Boolean(namensgleich)}>Verschieben</DialogSubmit>
        </DialogFooter>
      </DialogForm>
    </Dialog>
  );
}

// ── Datei umbenennen ─────────────────────────────────────────
export function DateiUmbenennenDialog({ dateiId, onClose }: { dateiId: string; onClose: () => void }) {
  const { bestand, daten, aendern } = useAblage();
  const datei = bestand.documents.find((d) => d.id === dateiId);
  const [basis, setBasis] = useState(datei ? grundname(datei.name) : "");
  const [fehler, setFehler] = useState<string | null>(null);
  if (!datei) return null;
  const e = endung(datei.name);

  return (
    <Dialog open onClose={onClose}>
      <DialogForm
        refresh={daten.quelle === "datenbank"}
        action={async () => {
          const name = e ? `${basis.trim()}.${e}` : basis.trim();
          const geschwister = bestand.documents.filter((d) => d.folderId === datei.folderId && d.id !== datei.id).map((d) => d.name);
          const f = basis.trim() ? namePruefen(name, geschwister, "Datei") : "Bitte einen Namen eingeben.";
          if (f) return { error: f };
          // Der Schluessel im Speicher bleibt, nur der Anzeigename aendert sich.
          const r = await dateiAendern(datei.id, { name, description: datei.description });
          if (istFehler(r)) return r;
          aendern((b) => mitDatei(b, datei.id, (d) => ({ ...d, name })));
          return { success: true as const };
        }}
        success="Umbenannt"
        onError={setFehler}
      >
        <DialogHeader>
          <DialogTitle>Datei umbenennen</DialogTitle>
          <DialogDescription>Die Endung bleibt, damit die Datei sich weiter mit dem richtigen Programm öffnet.</DialogDescription>
        </DialogHeader>
        <DialogContent>
          <div className="flex items-end gap-2">
            <Input
              label="Name"
              value={basis}
              onChange={(ev) => {
                setBasis(ev.target.value);
                setFehler(null);
              }}
              onFocus={(ev) => ev.currentTarget.select()}
              error={fehler ?? undefined}
              wrapperClassName="min-w-0 flex-1"
              autoComplete="off"
            />
            {e && <span className={cn("text-body-sm text-text-tertiary pb-2.5", fehler && "pb-8")}>.{e}</span>}
          </div>
        </DialogContent>
        <DialogFooter>
          <DialogCancel />
          <DialogSubmit>Speichern</DialogSubmit>
        </DialogFooter>
      </DialogForm>
    </Dialog>
  );
}

// ── Teilen-Link ──────────────────────────────────────────────
const FRISTEN = [
  { value: "7", label: "7 Tage" },
  { value: "30", label: "30 Tage" },
  { value: "90", label: "90 Tage" },
  { value: "datum", label: "Bis zu einem Datum" },
  { value: "nie", label: "Ohne Ablauf" },
];

/** Musterbestand: eine Kennung fuer die Anzeige (mit Sitzung vergibt sie der Server, 256 Bit Zufall). */
function zufallsKennung(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export async function inZwischenablage(text: string, meldung = "Kopiert") {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(meldung);
  } catch {
    toast.error("Kopieren ging nicht — bitte von Hand markieren.");
  }
}

export function TeilenDialog({ dateiId, onClose }: { dateiId: string; onClose: () => void }) {
  const { bestand, daten, aendern } = useAblage();
  const datei = bestand.documents.find((d) => d.id === dateiId);
  const [frist, setFrist] = useState("30");
  const [datum, setDatum] = useState(addTage(daten.today, 14));
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  // Mit Sitzung steht die Adresse NUR jetzt da — gespeichert ist allein ihre Pruefsumme.
  const [erstellt, setErstellt] = useState<string | null>(null);
  if (!datei) return null;
  const oeffentlich = wirksameSicht(datei.folderId, bestand.folders).visibility === "PUBLIC";
  const bis = frist === "nie" ? null : frist === "datum" ? datum : addTage(daten.today, Number(frist));

  if (erstellt) {
    return (
      <Dialog open onClose={onClose}>
        <DialogHeader>
          <DialogTitle>Link erstellt</DialogTitle>
          <DialogDescription>
            Für „{datei.name}“{bis ? `, gültig bis einschließlich ${formatDate(bis)}` : ", ohne Ablauf"}. Die Adresse steht nur jetzt hier — clubhub speichert sie nicht im Klartext. Geht sie verloren, einfach einen neuen Link erstellen.
          </DialogDescription>
        </DialogHeader>
        <DialogContent>
          <Input label="Adresse" value={erstellt} readOnly onFocus={(e) => e.currentTarget.select()} className="font-mono" />
        </DialogContent>
        <DialogFooter>
          <DialogCancel>Schließen</DialogCancel>
          <Button type="button" onClick={() => void inZwischenablage(erstellt, "Link kopiert")}>
            Kopieren
          </Button>
        </DialogFooter>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogForm
        refresh={daten.quelle === "datenbank"}
        keepOpen
        action={async () => {
          if (frist === "datum") {
            if (!datum) return { error: "Bitte ein Datum wählen." };
            if (datum <= daten.today) return { error: "Das Datum muss in der Zukunft liegen." };
          }
          const r = await linkErstellen(datei.id, { bis, allowDownload: laden });
          if (istFehler(r)) return r;
          const link: ShareLinkView = {
            id: neueId("l"),
            url: r.url ?? `https://clubhub.ansichtssache.de/d/${zufallsKennung()}`,
            expiresAt: bis,
            allowDownload: laden,
            createdAt: jetztIso(),
            createdBy: daten.me,
            revoked: false,
            views: 0,
          };
          aendern((b) => mitDatei(b, datei.id, (d) => ({ ...d, shares: [link, ...d.shares] })));
          setErstellt(link.url);
          await inZwischenablage(link.url!, "Link erstellt und kopiert");
          return { success: true as const };
        }}
        onError={setFehler}
      >
        <DialogHeader>
          <DialogTitle>Link teilen</DialogTitle>
          <DialogDescription>Für „{datei.name}“ — wer den Link hat, braucht keinen Zugang zu clubhub.</DialogDescription>
        </DialogHeader>
        <DialogContent>
          {oeffentlich && (
            <DialogNotice tone="accent">
              Die Datei liegt in einem öffentlichen Ordner. Ein Link lohnt sich vor allem, wenn ihn jemand ohne Zugang braucht oder er ablaufen soll.
            </DialogNotice>
          )}
          <Select label="Gültig" value={frist} onChange={setFrist} options={FRISTEN} />
          {frist === "datum" && (
            <div className="space-y-1.5">
              <FieldLabel htmlFor="teilen-bis">Gültig bis einschließlich</FieldLabel>
              <DateInput
                id="teilen-bis"
                value={datum}
                min={addTage(daten.today, 1)}
                onChange={(e) => {
                  setDatum(e.target.value);
                  setFehler(null);
                }}
                className={cn(
                  "bg-bg-primary text-body-sm text-text-primary flex h-10 w-full rounded-lg border px-3",
                  fehler ? "border-danger" : "border-border",
                )}
              />
              {fehler && <p className="text-caption text-danger">{fehler}</p>}
            </div>
          )}
          <Checkbox
            label="Herunterladen erlauben"
            hint="Sonst lässt sich die Datei nur im Browser ansehen (PDF, Bilder, Video)."
            checked={laden}
            onChange={(e) => setLaden(e.target.checked)}
          />
          <p className="text-caption text-text-tertiary">
            {bis ? `Der Link funktioniert bis einschließlich ${formatDate(bis)}.` : "Der Link funktioniert, bis du ihn widerrufst."}
          </p>
          {fehler && frist !== "datum" && <p className="text-caption text-danger">{fehler}</p>}
        </DialogContent>
        <DialogFooter>
          <DialogCancel />
          <DialogSubmit>Link erstellen und kopieren</DialogSubmit>
        </DialogFooter>
      </DialogForm>
    </Dialog>
  );
}

// ── Wohin hochladen (von der Startseite) ─────────────────────
export function HochladenZielDialog({ dateien, onClose }: { dateien: File[]; onClose: () => void }) {
  const { bestand, daten, hochladen, zumOrdner } = useAblage();
  const optionen = useMemo(() => ordnerOptionen(bestand.folders), [bestand.folders]);
  const [ziel, setZiel] = useState("");
  const sicht = ziel ? wirksameSicht(ziel, bestand.folders) : null;

  return (
    <Dialog open onClose={onClose}>
      <DialogForm
        refresh={false}
        action={() => {
          if (!ziel) return { error: "Bitte einen Ordner wählen." };
          return { success: true as const };
        }}
        onSuccess={() => {
          zumOrdner(ziel);
          hochladen(dateien, ziel);
        }}
        onError={(t) => toast.error(t)}
      >
        <DialogHeader>
          <DialogTitle>Wohin mit {dateien.length === 1 ? `„${dateien[0]!.name}“` : `${formatDecimal(dateien.length)} Dateien`}?</DialogTitle>
          <DialogDescription>Der Ordner bestimmt, wer die Dateien sehen darf.</DialogDescription>
        </DialogHeader>
        <DialogContent>
          <Select label="Ordner" value={ziel} onChange={setZiel} options={optionen} placeholder="Ordner wählen" searchable />
          {sicht && (
            <DialogNotice tone={sicht.visibility === "PUBLIC" ? "warning" : "neutral"}>
              <span className="font-medium">Sehen dürfen:</span> {sehenDuerfen(sicht, daten).satz}
            </DialogNotice>
          )}
          <ul className="text-caption text-text-tertiary space-y-0.5">
            {dateien.slice(0, 5).map((d, i) => (
              <li key={`${d.name}-${i}`} className="flex items-center gap-1.5 truncate">
                <ChevronRight className="h-3 w-3 shrink-0" aria-hidden />
                {d.name}
              </li>
            ))}
            {dateien.length > 5 && <li className="tabular-nums">und {formatDecimal(dateien.length - 5)} weitere</li>}
          </ul>
        </DialogContent>
        <DialogFooter>
          <DialogCancel />
          <DialogSubmit disabled={!ziel}>Hochladen</DialogSubmit>
        </DialogFooter>
      </DialogForm>
    </Dialog>
  );
}
